import React, { useState } from 'react';
import { Modal, UploadFile, message } from 'antd';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import iconClose from 'utils/icon/icon_close_circle.svg';
import { RcFile } from 'antd/lib/upload/interface';
import { handleMessage } from 'utils/helper/message';
import PrimaryButton from 'components/button/PrimaryButton';

import SelectBox from 'components/SelectBox';
import UploadMultiSound from './UploadMultiSound';
import { soundApis } from 'apis/resource/soundApis';
import { ICommonCode } from 'utils/types/storyTypes';

/**
 * * 리소스 - 미니컷 등록 모달
 */

interface IProps {
  visible: {
    create: boolean;
    edit: boolean;
    delete: boolean;
    name: string;
  };
  setVisible: (e: any) => void;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: boolean;
  projectId: string | undefined;
  charactersData: ICommonCode[] | null;
  soundTypeData: ICommonCode[] | null;
}

const SoundUploadModal = ({
  visible,
  setVisible,
  setRefresh,
  refresh,
  projectId,
  charactersData,
  soundTypeData,
}: IProps) => {
  const { t } = useTranslation();

  const [listSound, setListSound] = useState<UploadFile[] | []>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<any>({
    soundType: null,
    model: null,
  });

  /// 서버 전송
  const handleSubmit = async () => {
    if (projectId) {
      if (listSound.length > 0 && selected.model && selected.soundType) {
        const formData = new FormData();
        formData.append(`sound_type`, selected.soundType);
        formData.append(`speaker`, selected.model);
        listSound.forEach((image) => {
          formData.append(`files`, image as RcFile);
        });
        try {
          setLoading(true);
          await soundApis.soundUpload(projectId, formData);
          setRefresh(!refresh);
          setLoading(false);
          handleClose();
        } catch (err) {
          setLoading(false);
          handleMessage(message, 'error', 'The file name already has');
        }
      }
    }
  };

  const handleOnChangeSelect = (value, type: string) => {
    setSelected({ ...selected, [`${type}`]: value });
  };

  /// 모달 닫기
  const handleClose = () => {
    setVisible({ ...visible, create: false });
    setListSound([]);
    setSelected({
      model: null,
      soundType: null,
    });
  };
  return (
    <CustomModal
      title={t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.createModal.title',
      )}
      visible={visible.create}
      onCancel={handleClose}
      footer={
        <ButtonWrapper>
          <PrimaryButton
            text={t('buttons.create')}
            height='40px'
            width='fit-content'
            bgColor={theme.colors.blue_500}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.blue_500}`}
            rounded='6px'
            hoverBgColor={theme.colors.blue_600}
            activeBgColor={theme.colors.blue_800}
            selectedBgColor={theme.colors.blue_700}
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          />
        </ButtonWrapper>
      }
      closeIcon={
        <img src={iconClose} width={20} height={20} alt='icon-close' />
      }
    >
      <ModalWrapper>
        <div>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.createModal.soundType',
            )}
          </p>
          <SelectBox
            value={selected.soundType}
            onChange={(e) => {
              handleOnChangeSelect(e, 'soundType');
            }}
            options={soundTypeData?.map((v) => {
              return {
                value: v.code,
                label: v.code_name,
              };
            })}
          />
        </div>
        <div>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.createModal.character',
            )}
          </p>
          <SelectBox
            value={selected.model}
            onChange={(e) => {
              handleOnChangeSelect(e, 'model');
            }}
            options={charactersData?.map((v) => {
              return {
                value: v.code,
                label: v.code_name,
              };
            })}
          />
        </div>
        <UploadMultiSound
          listSound={listSound}
          setListSound={setListSound}
          requiredSubtitle={
            <ul>
              <li className='turText'>
                {t(
                  'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.minicutRegisterModal.requiredDuplicate',
                )}
              </li>
              <li className='turText'>
                {t(
                  'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.soundContent.requiredSubtitle',
                )}
              </li>
            </ul>
          }
        />
      </ModalWrapper>
    </CustomModal>
  );
};

export default SoundUploadModal;

const CustomModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-content {
    box-shadow: none;
    width: 566px;
  }
  .ant-radio-wrapper {
    span {
      font-size: ${theme.fontSizes.normal};
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.2px;
      color: ${theme.colors.dark_500};
    }
  }
  .ant-modal-footer {
    margin-top: 10px !important;
  }
  ul {
    margin-left: 25px;
  }
  .ant-modal-close:hover {
    background-color: transparent;
  }
`;

const ModalWrapper = styled.div`
  & > * + * {
    margin-top: 8px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
`;
